<!-- 验收表（移动端）｜逻辑与 PC 端 acceptanceTable.vue 保持一致，展示与交互按移动端重构 -->
<template>
  <div class="ma">
    <!-- 液冷测试项 -->
    <div v-if="isLiquidCooling" class="ma-test">
      <div class="ma-test__head">
        <span class="ma-test__title">{{ isDisabled ? '测试项数据' : '测试项录入' }}</span>
        <span class="ma-test__count">{{ testItemTableData.length }} 项</span>
      </div>

      <van-empty v-if="!testItemTableData.length" description="暂无测试项数据" image-size="60" />

      <div v-for="(item, index) in testItemTableData" :key="`ti-${index}`" class="ma-test__card">
        <div class="ma-test__card-head">
          <span class="ma-test__card-title">{{ item.testItem }}</span>
          <span class="ma-test__card-asset">{{ item.asset_id }}</span>
          <van-button v-if="!isDisabled" class="ma-test__icon-btn" icon="edit" size="mini" plain type="info"
            aria-label="编辑测试项" @click.stop="openTestItem(index)" />
          <van-button v-if="!isDisabled" class="ma-test__icon-btn ma-test__icon-btn--danger" icon="delete-o" size="mini"
            plain type="danger" aria-label="删除测试项" @click.stop="removeTestItem(index)" />
        </div>
        <div class="ma-test__pics">
          <div class="ma-test__pic">
            <van-image v-if="item.firstHydraulicPicture" :src="item.firstHydraulicPicture" width="56" height="56"
              fit="cover" radius="4" alt="第一次液压照片" @click="previewImage(item.firstHydraulicPicture)" />
            <div v-else class="ma-test__pic-empty">无图</div>
            <span class="ma-test__pic-label">第一次</span>
          </div>
          <div class="ma-test__pic">
            <van-image v-if="item.secondHydraulicPicture" :src="item.secondHydraulicPicture" width="56" height="56"
              fit="cover" radius="4" alt="第二次液压照片" @click="previewImage(item.secondHydraulicPicture)" />
            <div v-else class="ma-test__pic-empty">无图</div>
            <span class="ma-test__pic-label">第二次</span>
          </div>
          <div class="ma-test__numbers">
            <van-cell-group :border="false">
              <van-cell title="液压值" :border="false">
                <template #default>
                  <div class="ma-test__hyd">
                    <span>第一次 {{ item.firstHydraulic || '-' }}</span>
                    <span>第二次 {{ item.secondHydraulic || '-' }}</span>
                  </div>
                </template>
              </van-cell>
            </van-cell-group>
            <van-cell-group :border="false">
              <van-cell title="偏差" :border="false">
                <template #default>
                  <span :class="item.test_deviation_exceeded ? 'ma-row__value--danger' : 'ma-row__value--ok'">
                    {{ item.test_deviation || '-' }}
                  </span>
                  <em v-if="item.test_deviation_exceeded" class="ma-test__warn">超 5%</em>
                </template>
              </van-cell>
            </van-cell-group>
          </div>
        </div>
      </div>

      <van-button v-if="!isDisabled" block type="info" icon="plus" class="ma-test__add" @click="openTestItem">
        新增测试项
      </van-button>
    </div>

    <!-- 顶部汇总 -->

    <div class="ma__toolbar">
      <div class="ma__summary">
        <span class="ma__summary-num">{{ tableData.length }}</span>
        <span class="ma__summary-unit">条数据</span>
        <span class="ma__summary-dot">·</span>
        <span class="ma__summary-unit">已选 {{ selectData.length }}</span>
      </div>
      <van-button v-if="!isDisabled" class="ma__link" size="mini" plain type="info" @click="toggleSelectAll">
        {{ allSelected ? '取消全选' : '全选' }}
      </van-button>
    </div>

    <van-notice-bar v-if="!isDisabled" class="ma__tip" wrapable :scrollable="false" left-icon="info-o"
      text="未勾选数据时，设置结果默认应用于全部数据" />

    <!-- 操作区 -->
    <div v-if="!isDisabled" class="ma__ops">
      <van-button class="ma__op" type="info" @click="openResult">设置验收结果</van-button>
      <van-button class="ma__op ma__op--ghost" @click="openModuleNum">模块数量</van-button>
      <van-button class="ma__op ma__op--ghost" @click="openIsLine">是否接线</van-button>
    </div>

    <!-- 卡片列表 -->
    <div v-loading="is_loading" class="ma__list">
      <van-empty v-if="!visibleRows.length" description="暂无验收数据" />

      <div v-for="(row, index) in visibleRows" :key="rowKey(row, index)" class="ma-card"
        :class="{ 'ma-card--on': isSelected(row) }">
        <div class="ma-card__head">
          <div v-if="!isDisabled" class="ma-card__check" @click.stop="toggleSelect(row)">
            <van-checkbox :value="isSelected(row)" shape="square" />
          </div>
          <div class="ma-card__title" @click="toggleExpand(row)">
            <span class="ma-card__seq">{{ row.seq_id || index + 1 }}</span>
            <span class="ma-card__asset">{{ row.asset_id || '-' }}</span>
          </div>
          <van-tag v-if="row.confirm_result" :type="resultTagType(row.confirm_result)" plain>
            {{ row.confirm_result }}
          </van-tag>
          <van-icon :name="isExpanded(row) ? 'arrow-up' : 'arrow-down'" class="ma-card__arrow"
            @click="toggleExpand(row)" />
        </div>

        <div class="ma-card__body" @click="toggleExpand(row)">
          <van-cell-group :border="false">
            <van-cell title="SN" :value="row.sn || '-'" :border="false" />
            <van-cell title="机房" :value="row.idc_name || '-'" :border="false" />
            <van-cell title="机架/机位" :value="`${row.rack_name || '-'} / ${row.pos_code || '-'}`" :border="false" />
          </van-cell-group>
        </div>

        <van-cell-group v-if="isExpanded(row)" class="ma-card__extra" :border="false">
          <van-cell title="验收模块数" :value="String(row.check_module_num || 0)" :border="false" />
          <van-cell title="下发模块数" :value="row.module_num || '-'" :border="false" />
          <van-cell title="是否接线" :value="row.check_is_line || '是'" :border="false" />
          <van-cell title="物理上架" :value="row.bare_metal" :border="false" />
          <van-cell title="设备型号" :value="row.server_model || '-'" :border="false" />
          <van-cell title="多节点" :value="row.is_sanjiang || '-'" :border="false" />
          <van-cell title="关联固资" :value="row.sanjiang_assetid || '-'" :border="false" />
          <van-cell v-if="row.confirm_fail_reason" title="原因" :value="row.confirm_fail_reason"
            value-class="ma-row__value--warn" :border="false" />
          <van-cell title="标准插线图" :border="false">
            <template #default>
              <span v-if="row.docUrl" class="ma-row__value--link" @click.stop="openLink(row.docUrl)">
                {{ row.docUrl }}
              </span>
              <span v-else class="ma-row__value">无特殊要求</span>
            </template>
          </van-cell>
        </van-cell-group>
      </div>

      <van-button v-if="hasMore" block plain type="info" class="ma__more" @click="loadMore">
        加载更多（剩余 {{ tableData.length - displayedCount }} 条）
      </van-button>
    </div>

    <!-- 验收结果弹层 -->
    <van-popup v-model="resultVisible" position="bottom" round :style="{ maxHeight: '85%' }" closeable class="ma-pop"
      get-container="body" :z-index="popupZIndex">
      <div class="ma-pop__inner">
        <div class="ma-pop__title">设置本次验收结果</div>
        <div class="ma-pop__hint" :class="{ 'ma-pop__hint--warn': isScopeAll }">{{ scopeText }}</div>
        <div class="ma-pop__label">验收结果</div>
        <van-radio-group v-model="formParms.checkResult" direction="horizontal" class="ma-radios"
          @change="onPickResult">
          <van-radio v-for="opt in checkOptions" :key="opt.value" :name="opt.value" checked-color="#1989fa"
            class="ma-radios__item">
            {{ opt.label }}
          </van-radio>
        </van-radio-group>
        <template v-if="needReason">
          <div class="ma-pop__label">{{ formParms.checkResult }}原因</div>
          <van-radio-group v-if="reasonOptions.length" v-model="formParms.errorResult" direction="horizontal"
            class="ma-radios ma-radios--wrap">
            <van-radio v-for="opt in reasonOptions" :key="opt.value" :name="opt.value" checked-color="#1989fa"
              class="ma-radios__item">
              {{ opt.label }}
            </van-radio>
          </van-radio-group>
          <van-empty v-else description="暂无可选原因" image-size="60" />
        </template>
        <div class="ma-pop__footer">
          <van-button block type="info" :loading="submitting" @click="onSubmit">确定</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 模块数量弹层 -->
    <van-popup v-model="moduleVisible" position="bottom" round closeable class="ma-pop" get-container="body"
      :z-index="popupZIndex">
      <div class="ma-pop__inner">
        <div class="ma-pop__title">设置确认模块数量</div>
        <div class="ma-pop__hint" :class="{ 'ma-pop__hint--warn': isScopeAll }">{{ scopeText }}</div>

        <div class="ma-num">
          <span class="ma-num__label">验收模块数量</span>
          <van-stepper
            v-model="commonParms.check_module_num"
            :min="0"
            :max="moduleNumMax"
            integer
            input-width="56px"
            button-size="30px"
            @change="onModuleNumChange"
            @blur="onModuleNumBlur"
          />
        </div>

        <!-- <div class="ma-num__tip" :class="{ 'ma-num__tip--error': !!moduleNumError }">
          {{ moduleNumError || moduleNumTip }}
        </div> -->

        <div v-if="refModuleNum" class="ma-pop__quick">
          <van-button size="small" plain type="info" @click="fillRefModuleNum">
            填入下发值 {{ refModuleNum }} 个
          </van-button>
        </div>

        <div class="ma-pop__footer">
          <van-button block type="info" @click="setFieldsSubmit('module')">确定</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 是否接线弹层 -->
    <van-popup v-model="lineVisible" position="bottom" round closeable class="ma-pop" get-container="body"
      :z-index="popupZIndex">
      <div class="ma-pop__inner">
        <div class="ma-pop__title">设置是否接线</div>
        <div class="ma-pop__hint" :class="{ 'ma-pop__hint--warn': isScopeAll }">{{ scopeText }}</div>
        <van-radio-group v-model="commonParms.check_is_line" direction="horizontal" class="ma-radios">
          <van-radio v-for="opt in lineOptions" :key="opt" :name="opt" checked-color="#1989fa" class="ma-radios__item">
            {{ opt }}
          </van-radio>
        </van-radio-group>
        <div class="ma-pop__footer">
          <van-button block type="info" @click="setFieldsSubmit('line')">确定</van-button>
        </div>
      </div>
    </van-popup>

    <!-- 测试项录入弹层 -->
    <van-popup v-model="testItemVisible" position="bottom" round :style="{ maxHeight: '90%' }" closeable class="ma-pop"
      get-container="body" :z-index="popupZIndex">
      <div class="ma-pop__inner ma-pop__inner--scroll">
        <div class="ma-pop__title">{{ isEditing ? '编辑测试项' : '新增测试项' }}</div>
        <van-field v-model="testItemInput.assetId" label="固资号" placeholder="请输入固资号" input-align="right"
          :readonly="isEditing" @blur="matchTestItem" />
        <van-field v-model="testItemInput.testName" label="测试项名称" placeholder="请输入测试项名称" input-align="right"
          :readonly="isEditing" @blur="matchTestItem" />
        <van-field v-model="testItemInput.firstHydraulic" type="number" label="第一次液压" placeholder="请输入第一次液压数值"
          input-align="right" @input="calcInputDeviation" />
        <van-cell-group :border="false" class="ma-upload">
          <van-cell title="第一次照片" :border="false">
            <template #default>
              <van-uploader v-model="testItemInput.firstPictureList" :max-count="1" accept="image/*"
                :max-size="IMAGE_MAX_SIZE" :before-read="beforeImageRead" :preview-image="false"
                @click-preview="onPreviewUploadItem" @oversize="onImageOversize"
                :after-read="onFirstPictureRead" @delete="handleImageRemove('firstPicture')" />
            </template>
          </van-cell>
        </van-cell-group>
        <van-field v-model="testItemInput.secondHydraulic" type="number" label="第二次液压" placeholder="请输入第二次液压数值"
          input-align="right" @input="calcInputDeviation" />
        <van-cell-group :border="false" class="ma-upload">
          <van-cell title="第二次照片" :border="false">
            <template #default>
              <van-uploader v-model="testItemInput.secondPictureList" :max-count="1" accept="image/*"
                :max-size="IMAGE_MAX_SIZE" :before-read="beforeImageRead" :preview-image="false"
                @click-preview="onPreviewUploadItem" @oversize="onImageOversize"
                :after-read="onSecondPictureRead" @delete="handleImageRemove('secondPicture')" />
            </template>
          </van-cell>
        </van-cell-group>
        <div class="ma-dev">
          <span class="ma-dev__label">偏差</span>
          <span class="ma-dev__value" :class="inputDeviation.exceeded ? 'ma-dev__value--danger' : 'ma-dev__value--ok'">
            {{ inputDeviation.text }}
          </span>
          <span v-if="inputDeviation.exceeded" class="ma-dev__warn">超过 5% 阈值，该项不允许结单</span>
        </div>
        <div class="ma-pop__footer">
          <van-button block type="info" @click="addTestItem">添加</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import axios from 'axios';
import { ImagePreview, Dialog, Toast } from 'vant';
import { rejectSelect, delaySelect } from '@/api/operateData/acceptanceApi';

const PAGE_STEP = 20;
const POPUP_Z_INDEX = 99999;
const FEEDBACK_Z_INDEX = 100001;
// 单张图片上限 10M，与 reformScan / uploadNew 保持一致
const IMAGE_MAX_SIZE = 10 * 1024 * 1024;
const IMAGE_ALLOW_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

export default {
  name: 'acceptanceTableMobile',
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    toggle: {
      type: Boolean,
      default: false,
    },
    is_loading: {
      type: Boolean,
      default: false,
    },
    is_liquid_cooling: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      tableData: [],
      selectData: [],
      expandedRows: [],
      displayedCount: PAGE_STEP,
      popupZIndex: POPUP_Z_INDEX,

      // 验收结果
      resultVisible: false,
      formParms: { checkResult: '', errorResult: '' },
      checkOptions: [
        { value: '验收成功', label: '验收成功' },
        { value: '验收失败', label: '验收失败' },
        { value: '延迟验收', label: '延迟验收' },
      ],
      checkReason: [],
      delaySelectOptions: [],
      submitting: false,

      // 通用设置
      moduleVisible: false,
      lineVisible: false,
      lineOptions: ['是', '否'],
      commonParms: { check_module_num: 0, check_is_line: '是' },
      moduleNumError: '',

      // 测试项
      testItemVisible: false,
      editingIndex: -1,
      testItemTableData: [],
      testItemInput: {
        assetId: '',
        testName: '',
        firstHydraulic: '',
        secondHydraulic: '',
        firstPicture: '',
        firstPictureList: [],
        secondPicture: '',
        secondPictureList: [],
      },
      inputDeviation: { text: '-', exceeded: false },
      // 暴露给模板使用（单张图片上限 10M）
      IMAGE_MAX_SIZE,
    };
  },
  computed: {
    // Formily 通过 attrs 透传 disabled
    isDisabled() {
      return !!this.$attrs.disabled;
    },
    isLiquidCooling() {
      return this.is_liquid_cooling == 1; // eslint-disable-line eqeqeq
    },
    visibleRows() {
      return this.tableData.slice(0, this.displayedCount);
    },
    hasMore() {
      return this.displayedCount < this.tableData.length;
    },
    allSelected() {
      return this.tableData.length > 0 && this.selectData.length === this.tableData.length;
    },
    needReason() {
      return ['验收失败', '延迟验收'].includes(this.formParms.checkResult);
    },
    reasonOptions() {
      const list = this.formParms.checkResult === '验收失败' ? this.checkReason : this.delaySelectOptions;
      if (!Array.isArray(list)) return [];
      return list.map((item) =>
        typeof item === 'string'
          ? { label: item, value: item }
          : { label: item.label || item.value, value: item.value === undefined ? item.label : item.value }
      );
    },
    scopeText() {
      const count = this.selectData.length || this.tableData.length;
      return this.selectData.length
        ? `将应用于已选 ${count} 条数据`
        : `未勾选，将应用于全部 ${count} 条数据`;
    },
    // 未勾选时按全量生效，需要在 UI 上给出明确警示
    isScopeAll() {
      return this.selectData.length === 0 && this.tableData.length > 0;
    },
    // 是否处于编辑测试项状态，避免模板里直接写比较运算符
    isEditing() {
      return this.editingIndex > -1;
    },
    // ===== 模块数量 =====
    moduleNumMax() {
      return 999;
    },
    // 取目标行里“下发模块数”的最大值，作为填写参考
    refModuleNum() {
      const nums = this.targetRows()
        .map((row) => Number(row.module_num))
        .filter((n) => Number.isFinite(n) && n > 0);
      return nums.length ? Math.max(...nums) : 0;
    },
    moduleNumTip() {
      if (!this.refModuleNum) return `范围 0-${this.moduleNumMax}`;
      return `范围 0-${this.moduleNumMax}，本次下发模块数为 ${this.refModuleNum}`;
    },
  },
  watch: {
    value: {
      handler(val) {
        this.$nextTick(() => {
          this.tableData = (Array.isArray(val) ? val : []).map((item) => {
            item.check_module_num = !item.check_module_num ? 0 : item.check_module_num;
            item.check_is_line = !item.check_is_line ? '是' : item.check_is_line;
            item.bare_metal = item.bare_metal === 1 ? '是' : '-';
            return item;
          });
          this.initTestItemFromTable();
          this.displayedCount = PAGE_STEP;
        });
      },
      deep: true,
      immediate: true,
    },
    toggle: {
      handler() {
        this.$nextTick(() => {
          this.selectData = [];
          this.tableData = [...this.tableData];
        });
      },
      deep: true,
    },
  },
  created() {
    Toast.setDefaultOptions({ zIndex: FEEDBACK_Z_INDEX });
    Dialog.setDefaultOptions({ zIndex: FEEDBACK_Z_INDEX });
    this.loadReasonOptions();
  },
  mounted() {
    this.$emit('selection-change', this.tableData);
  },
  methods: {
    emptyTestItem() {
      return {
        assetId: '',
        testName: '',
        firstHydraulic: '',
        secondHydraulic: '',
        firstPicture: '',
        firstPictureList: [],
        secondPicture: '',
        secondPictureList: [],
      };
    },

    // ===== 失败/延迟原因选项 =====
    async loadReasonOptions() {
      try {
        const [rejectRes, delayRes] = await Promise.allSettled([rejectSelect(), delaySelect()]);
        if (rejectRes.status === 'fulfilled') {
          this.checkReason = rejectRes.value?.result || [];
        }
        if (delayRes.status === 'fulfilled') {
          this.delaySelectOptions = delayRes.value?.result || [];
        }
      } catch (error) {
        console.log('[acceptanceTable] 原因选项加载失败', error);
      }
    },

    // ===== 列表基础 =====
    rowKey(row, index) {
      return `${row.seq_id || ''}-${row.asset_id || ''}-${index}`;
    },
    resultTagType(result) {
      if (result === '验收成功') return 'success';
      if (result === '验收失败') return 'danger';
      return 'warning';
    },
    toggleExpand(row) {
      const i = this.expandedRows.indexOf(row);
      if (i > -1) {
        this.expandedRows.splice(i, 1);
      } else {
        this.expandedRows.push(row);
      }
    },
    isExpanded(row) {
      return this.expandedRows.indexOf(row) > -1;
    },
    loadMore() {
      this.displayedCount += PAGE_STEP;
    },
    openLink(url) {
      window.open(this.$processImageUrl ? this.$processImageUrl(url) : url);
    },
    previewImage(url) {
      if (!url) return;
      ImagePreview({
        images: [this.$processImageUrl ? this.$processImageUrl(url) : url],
        closeable: true,
        zIndex: FEEDBACK_Z_INDEX,
      });
    },
    onPreviewUploadItem(item) {
      const url = item && (item.url || item.content);
      if (url) this.previewImage(url);
    },

    // ===== 选择 =====
    isSelected(row) {
      return this.selectData.indexOf(row) > -1;
    },
    toggleSelect(row) {
      const i = this.selectData.indexOf(row);
      if (i > -1) {
        this.selectData.splice(i, 1);
      } else {
        this.selectData.push(row);
      }
      this.$emit('selection-change', this.selectData);
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectData = [];
      } else {
        this.selectData = [...this.tableData];
      }
      this.$emit('selection-change', this.selectData);
    },

    // ===== 目标行：勾选优先，否则全量 =====
    targetRows() {
      return this.selectData.length > 0 ? this.selectData : this.tableData;
    },
    resetSelection() {
      this.selectData = [];
    },

    // ===== 验收结果 =====
    openResult() {
      if (!this.tableData.length) {
        this.$toast('请先扫描数据');
        return;
      }
      this.formParms = { checkResult: '', errorResult: '' };
      this.resultVisible = true;
    },
    // van-radio-group 的 change 回调：切换结果时清空已选原因
    onPickResult(val) {
      this.formParms.errorResult = '';
      if (!val) return;
    },
    onSubmit() {
      if (!this.formParms.checkResult) {
        this.$toast('请选择验收结果');
        return;
      }
      if (this.needReason && !this.formParms.errorResult) {
        this.$toast(`请选择${this.formParms.checkResult}原因`);
        return;
      }
      this.confirmScopeAll('applyResult');
    },
    // 未勾选时按全量生效，提交前给出二次确认，避免误改全部数据
    confirmScopeAll(callbackName) {
      if (!this.isScopeAll) {
        this[callbackName]();
        return;
      }
      const count = this.tableData.length;
      Dialog.confirm({
        title: '未勾选任何数据',
        message: `当前未勾选，本次设置将作用于全部 ${count} 条数据，是否继续？`,
        confirmButtonText: '继续设置',
        cancelButtonText: '先去勾选',
      })
        .then(() => {
          this[callbackName]();
        })
        .catch(() => { });
    },
    applyResult() {
      this.submitting = true;
      try {
        this.targetRows().forEach((item) => {
          this.$set(item, 'confirm_result', this.formParms.checkResult);
          this.$set(
            item,
            'confirm_fail_reason',
            this.needReason ? this.formParms.errorResult : ''
          );
        });
        this.resetSelection();
        this.resultVisible = false;
        this.$toast('设置成功');
        this.$emit('selection-change', this.tableData);
      } finally {
        this.submitting = false;
      }
    },

    // ===== 模块数量 / 是否接线 =====
    openModuleNum() {
      if (!this.tableData.length) {
        this.$toast('请先扫描数据');
        return;
      }
      // 默认填 0；若存在下发模块数（>0）则优先预填下发值
      const refNum = Number(this.refModuleNum);
      this.commonParms.check_module_num = String(refNum > 0 ? refNum : 0);
      this.moduleNumError = '';
      this.moduleVisible = true;
    },
    // 输入变化：实时校验范围
    onModuleNumChange(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return;
      this.moduleNumError = '';
      if (n < 0) this.moduleNumError = '模块数量不能小于 0';
      else if (n > this.moduleNumMax) this.moduleNumError = `模块数量不能超过 ${this.moduleNumMax}`;
    },
    // 失焦时归一：空值或负数统一回落到 0
    onModuleNumBlur() {
      const raw = this.commonParms.check_module_num;
      if (raw === '' || raw === null || raw === undefined || Number(raw) < 0) {
        this.commonParms.check_module_num = '0';
        this.moduleNumError = '';
      }
    },
    fillRefModuleNum() {
      if (!this.refModuleNum) return;
      this.commonParms.check_module_num = String(this.refModuleNum);
      this.moduleNumError = '';
    },
    // 校验并返回合法数值；不合法返回 null
    validateModuleNum() {
      const raw = this.commonParms.check_module_num;
      if (raw === '' || raw === null || raw === undefined) {
        this.moduleNumError = '请输入验收模块数量';
        this.$toast('请输入验收模块数量');
        return null;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        this.moduleNumError = '模块数量必须是整数';
        this.$toast('模块数量必须是整数');
        return null;
      }
      if (n < 0) {
        this.moduleNumError = '模块数量不能小于 0';
        this.$toast('模块数量不能小于 0');
        return null;
      }
      if (n > this.moduleNumMax) {
        this.moduleNumError = `模块数量不能超过 ${this.moduleNumMax}`;
        this.$toast(`模块数量不能超过 ${this.moduleNumMax}`);
        return null;
      }
      this.moduleNumError = '';
      return n;
    },
    openIsLine() {
      if (!this.tableData.length) {
        this.$toast('请先扫描数据');
        return;
      }
      this.commonParms.check_is_line = '是';
      this.lineVisible = true;
    },
    setFieldsSubmit(type) {
      if (type === 'module') {
        const num = this.validateModuleNum();
        if (num === null) return;
        this.confirmScopeAll('applyModuleNum');
        return;
      }
      this.confirmScopeAll('applyIsLine');
    },
    applyModuleNum() {
      const num = Number(this.commonParms.check_module_num);
      this.targetRows().forEach((item) => {
        this.$set(item, 'check_module_num', num);
      });
      this.resetSelection();
      this.moduleVisible = false;
      this.$toast('设置成功');
      this.$emit('selection-change', this.tableData);
    },
    applyIsLine() {
      const fieldName = 'check_is_line';
      const fieldValue = this.commonParms.check_is_line;
      this.targetRows().forEach((item) => {
        this.$set(item, fieldName, fieldValue);
      });
      this.resetSelection();
      this.lineVisible = false;
      this.$toast('设置成功');
      this.$emit('selection-change', this.tableData);
    },

    // ===== 测试项 =====
    initTestItemFromTable() {
      const list = [];
      this.tableData.forEach((row) => {
        if (Array.isArray(row.liquidCoolingAcceptance) && row.liquidCoolingAcceptance.length) {
          row.liquidCoolingAcceptance.forEach((ti) => {
            list.push({
              asset_id: row.asset_id || '',
              testItem: ti.testItem || '',
              firstHydraulicPicture: ti.firstHydraulicPicture || '',
              firstHydraulic: ti.firstHydraulic || '',
              secondHydraulicPicture: ti.secondHydraulicPicture || '',
              secondHydraulic: ti.secondHydraulic || '',
              test_deviation: ti.test_deviation || '',
              test_deviation_exceeded: ti.test_deviation_exceeded || false,
            });
          });
        }
      });
      // 与 PC 一致：只有后端返回了测试项时才覆盖
      if (list.length) {
        this.testItemTableData = list;
      }
    },
    openTestItem(index) {
      if (!this.tableData.length) {
        this.$toast('请先扫描数据');
        return;
      }
      if (typeof index === 'number' && index > -1 && this.testItemTableData[index]) {
        const item = this.testItemTableData[index];
        this.editingIndex = index;
        this.testItemInput = {
          assetId: item.asset_id || '',
          testName: item.testItem || '',
          firstHydraulic: item.firstHydraulic || '',
          secondHydraulic: item.secondHydraulic || '',
          firstPicture: item.firstHydraulicPicture || '',
          firstPictureList: item.firstHydraulicPicture
            ? [{ url: item.firstHydraulicPicture, isImage: true, status: 'done' }]
            : [],
          secondPicture: item.secondHydraulicPicture || '',
          secondPictureList: item.secondHydraulicPicture
            ? [{ url: item.secondHydraulicPicture, isImage: true, status: 'done' }]
            : [],
        };
        this.calcInputDeviation();
      } else {
        this.editingIndex = -1;
        this.testItemInput = this.emptyTestItem();
        this.inputDeviation = { text: '-', exceeded: false };
      }
      this.testItemVisible = true;
    },
    // 固资号 + 测试项名称匹配回填
    matchTestItem() {
      const assetId = (this.testItemInput.assetId || '').trim();
      const testName = (this.testItemInput.testName || '').trim();
      if (!assetId || !testName) return;

      const matched = this.testItemTableData.find(
        (item) => item.asset_id === assetId && item.testItem === testName
      );
      if (matched) {
        this.fillTestItem(matched);
        this.$toast(`已关联测试项：${testName}`);
        return;
      }

      const mainRow = this.tableData.find((r) => r.asset_id === assetId);
      if (mainRow && Array.isArray(mainRow.liquidCoolingAcceptance)) {
        const backendItem = mainRow.liquidCoolingAcceptance.find((ti) => ti.testItem === testName);
        if (backendItem) {
          this.fillTestItem({
            asset_id: assetId,
            testItem: testName,
            firstHydraulicPicture: backendItem.firstHydraulicPicture,
            firstHydraulic: backendItem.firstHydraulic,
            secondHydraulicPicture: backendItem.secondHydraulicPicture,
            secondHydraulic: backendItem.secondHydraulic,
          });
          this.$toast(`已关联后端测试项：${testName}`);
        }
      }
    },
    fillTestItem(item) {
      this.testItemInput.firstHydraulic = item.firstHydraulic || '';
      this.testItemInput.secondHydraulic = item.secondHydraulic || '';
      this.testItemInput.firstPicture = item.firstHydraulicPicture || '';
      this.testItemInput.firstPictureList = item.firstHydraulicPicture
        ? [{ url: item.firstHydraulicPicture, isImage: true, status: 'done' }]
        : [];
      this.testItemInput.secondPicture = item.secondHydraulicPicture || '';
      this.testItemInput.secondPictureList = item.secondHydraulicPicture
        ? [{ url: item.secondHydraulicPicture, isImage: true, status: 'done' }]
        : [];
      this.calcInputDeviation();
    },
    calcInputDeviation() {
      const { firstHydraulic, secondHydraulic } = this.testItemInput;
      this.inputDeviation = this.calcTestDeviation(firstHydraulic, secondHydraulic);
    },
    // 偏差 |第一次 - 第二次| / 第一次
    calcTestDeviation(firstHydraulic, secondHydraulic) {
      const first = parseFloat(firstHydraulic);
      const second = parseFloat(secondHydraulic);
      if (isNaN(first) || isNaN(second) || first === 0) {
        return { text: first === 0 ? '第一次数值不能为0' : '-', exceeded: false };
      }
      const deviation = Math.abs(first - second) / Math.abs(first);
      return { text: `${(deviation * 100).toFixed(2)}%`, exceeded: deviation > 0.05 };
    },
    addTestItem() {
      const { assetId, testName, firstHydraulic, secondHydraulic } = this.testItemInput;

      if (!assetId || !assetId.trim()) {
        this.$toast('请输入固资号');
        return;
      }
      if (!testName || !testName.trim()) {
        this.$toast('请输入测试项名称');
        return;
      }
      if ((!firstHydraulic && firstHydraulic !== 0) || isNaN(parseFloat(firstHydraulic))) {
        this.$toast('请输入有效的第一次液压数值');
        return;
      }
      if ((!secondHydraulic && secondHydraulic !== 0) || isNaN(parseFloat(secondHydraulic))) {
        this.$toast('请输入有效的第二次液压数值');
        return;
      }
      if (this.isImageUploading('firstPicture')) {
        this.$toast('第一次照片上传中，请稍候');
        return;
      }
      if (this.isImageUploading('secondPicture')) {
        this.$toast('第二次照片上传中，请稍候');
        return;
      }
      if (!this.testItemInput.firstPicture) {
        this.$toast('请上传第一次液压照片');
        return;
      }
      if (!this.testItemInput.secondPicture) {
        this.$toast('请上传第二次液压照片');
        return;
      }
      const exists = this.tableData.some((item) => item.asset_id && item.asset_id === assetId.trim());
      if (!exists) {
        this.$toast(`未找到固资号为 "${assetId}" 的数据`);
        return;
      }

      const { text: deviationText, exceeded: deviationExceeded } = this.calcTestDeviation(
        firstHydraulic,
        secondHydraulic
      );
      const itemData = {
        asset_id: assetId.trim(),
        testItem: testName.trim(),
        firstHydraulicPicture: this.testItemInput.firstPicture || '',
        firstHydraulic: firstHydraulic || '',
        secondHydraulicPicture: this.testItemInput.secondPicture || '',
        secondHydraulic: secondHydraulic || '',
        test_deviation: deviationText,
        test_deviation_exceeded: deviationExceeded,
      };
      const existingIndex =
        this.editingIndex > -1
          ? this.editingIndex
          : this.testItemTableData.findIndex(
            (item) => item.asset_id === assetId.trim() && item.testItem === testName.trim()
          );
      if (existingIndex >= 0) {
        this.$set(this.testItemTableData, existingIndex, itemData);
      } else {
        this.testItemTableData.push(itemData);
      }

      this.$toast(
        deviationExceeded
          ? `偏差 ${deviationText}，超过 5% 阈值，该项不允许结单`
          : existingIndex >= 0
            ? '已更新该测试项'
            : '添加成功'
      );

      // 保留固资号，便于连续录入同一固资号
      this.testItemInput = { ...this.emptyTestItem(), assetId };
      this.inputDeviation = { text: '-', exceeded: false };
      this.editingIndex = -1;
      this.testItemVisible = false;
      this.emitTestItemData();
    },
    removeTestItem(index) {
      this.testItemTableData.splice(index, 1);
      this.$toast('删除成功');
      this.emitTestItemData();
    },
    // 按固资号分组挂到主表行的 liquidCoolingAcceptance
    emitTestItemData() {
      const groupMap = {};
      this.testItemTableData.forEach((item) => {
        if (!groupMap[item.asset_id]) groupMap[item.asset_id] = [];
        groupMap[item.asset_id].push({
          testItem: item.testItem,
          firstHydraulicPicture: item.firstHydraulicPicture,
          firstHydraulic: item.firstHydraulic,
          secondHydraulicPicture: item.secondHydraulicPicture,
          secondHydraulic: item.secondHydraulic,
          test_deviation: item.test_deviation,
          test_deviation_exceeded: item.test_deviation_exceeded,
        });
      });
      this.tableData.forEach((row) => {
        this.$set(row, 'liquidCoolingAcceptance', []);
      });
      Object.keys(groupMap).forEach((assetId) => {
        const row = this.tableData.find((r) => r.asset_id === assetId);
        if (row) this.$set(row, 'liquidCoolingAcceptance', groupMap[assetId]);
      });
      this.$emit('selection-change', this.tableData);
    },

    // ===== 图片上传 =====
    /** 选图前校验：仅允许 jpg/png/jpeg 图片，与 reformScan 一致 */
    beforeImageRead(file) {
      const ok = Array.isArray(file)
        ? file.every((f) => IMAGE_ALLOW_TYPES.includes(f.type))
        : IMAGE_ALLOW_TYPES.includes(file.type);
      if (!ok) this.$toast('仅支持上传图片（jpg/png）');
      return ok;
    },
    /** 超出单张大小上限提示 */
    onImageOversize() {
      this.$toast('文件大小不能超过 10M');
    },
    async handleImageRead(file, field) {
      const item = Array.isArray(file) ? file[0] : file;
      if (!item) return;
      // 上传前把“上传中”状态挂到列表项，供提交时拦截
      item.status = 'uploading';
      item.message = '上传中';
      this.testItemInput[`${field}List`] = [{ ...item, status: 'uploading', message: '上传中' }];
      try {
        const formData = new FormData();
        formData.append('file', item.file);
        const res = await axios.post('/relay/cosfile/index', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authToken: sessionStorage.getItem('AuthToken'),
            userid: sessionStorage.getItem('userid'),
          },
        });
        const uploaded = (res.data && res.data.FileList && res.data.FileList[0]) || {};
        if (res.status === 200 && uploaded.url) {
          this.testItemInput[field] = uploaded.url;
          this.testItemInput[`${field}List`] = [
            {
              url: uploaded.url,
              isImage: true,
              status: 'done',
              message: '上传成功',
              fileName: uploaded.name || (item.file && item.file.name) || '',
            },
          ];
          this.$toast('上传成功');
        } else {
          throw new Error('上传失败');
        }
      } catch (error) {
        // 失败时清空，避免把空 url 当成已上传
        this.testItemInput[field] = '';
        this.testItemInput[`${field}List`] = [];
        this.$toast('图片上传失败，请重新上传');
      }
    },
    handleImageRemove(field) {
      this.testItemInput[field] = '';
      this.testItemInput[`${field}List`] = [];
    },
    // 模板中直接引用具名方法，避免在模板属性值里写箭头函数
    onFirstPictureRead(file) {
      return this.handleImageRead(file, 'firstPicture');
    },
    onSecondPictureRead(file) {
      return this.handleImageRead(file, 'secondPicture');
    },
    /** 判断某个照片字段是否仍在上传中，提交前拦截 */
    isImageUploading(field) {
      const list = this.testItemInput[`${field}List`] || [];
      return list.some((f) => f.status === 'uploading');
    },
  },
};
</script>

<style scoped lang="less">
.ma {
  padding: 12px;
  background: #f7f8fa;
  box-sizing: border-box;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 10px;
  }

  &__summary {
    display: flex;
    align-items: baseline;
    color: #646566;
    font-size: 13px;
  }

  &__summary-num {
    font-size: 18px;
    font-weight: 600;
    color: #323233;
    margin-right: 2px;
  }

  &__summary-dot {
    margin: 0 6px;
    color: #c8c9cc;
  }

  &__link {
    min-height: 32px;
    font-size: 13px;
  }

  // 父级 formily 表单把 .van-button--info 背景强制成蓝色，plain 按钮会变成蓝底蓝字
  &__toolbar &__link.van-button--plain {
    background-color: #fff;
    color: #1989fa;
    border-color: #1989fa;
  }

  &__tip {
    margin-bottom: 10px;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1.5;
    padding: 8px 10px;
    background: #ecf5ff;
    color: #3d7eff;

    ::v-deep .van-notice-bar__wrap {
      align-items: flex-start;
    }

    ::v-deep .van-notice-bar__content {
      text-align: left;
    }
  }

  &__list {
    min-height: 80px;
  }

  &__more {
    margin: 12px 0 4px;
    font-size: 13px;
  }

  &__ops {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__op {
    flex: 1;
    min-height: 44px;
    border-radius: 8px;
    font-size: 13px;
    padding: 0 8px;

    &--ghost {
      flex: 0 0 88px;
      color: #323233;
      border-color: #dcdee0;
      background: #fff;
    }
  }
}

/* 卡片 */
.ma-card {
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &--on {
    border-color: #1989fa;
    box-shadow: 0 2px 10px rgba(25, 137, 250, 0.12);
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__check {
    flex-shrink: 0;
    min-width: 32px;
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  &__title {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
  }

  &__seq {
    flex-shrink: 0;
    min-width: 22px;
    height: 22px;
    line-height: 22px;
    text-align: center;
    border-radius: 11px;
    background: #f2f3f5;
    color: #969799;
    font-size: 12px;
  }

  &__asset {
    font-size: 15px;
    font-weight: 600;
    color: #323233;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    flex-shrink: 0;
    color: #969799;
    font-size: 14px;
    padding: 8px 2px;
  }

  &__body {
    margin-top: 6px;
    padding-left: 30px;

    ::v-deep .van-cell {
      padding: 5px 0;
      font-size: 13px;
      line-height: 1.5;
      background: transparent;
    }

    ::v-deep .van-cell__title {
      flex: 0 0 84px;
      max-width: 84px;
      color: #969799;
    }

    ::v-deep .van-cell__value {
      color: #323233;
      word-break: break-all;
    }
  }

  &__extra {
    margin-top: 4px;
    padding-top: 6px;
    padding-left: 30px;
    border-top: 1px dashed #ebedf0;

    ::v-deep .van-cell {
      padding: 5px 0;
      font-size: 13px;
      line-height: 1.5;
      background: transparent;
    }

    ::v-deep .van-cell__title {
      flex: 0 0 84px;
      max-width: 84px;
      color: #969799;
    }

    ::v-deep .van-cell__value {
      color: #323233;
      word-break: break-all;
    }
  }
}

/* 字段值修饰类（配合 van-cell 使用） */
.ma-row__value {
  color: #323233;
  word-break: break-all;

  &--warn {
    color: #ed6a0c;
  }

  &--link {
    color: #1989fa;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &--danger {
    color: #ee0a24;
    font-weight: 600;
  }

  &--ok {
    color: #07c160;
    font-weight: 600;
  }
}

/* 测试项 */
.ma-test {
  margin-top: 0;
  margin-bottom: 16px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 4px 8px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #323233;
  }

  &__count {
    font-size: 12px;
    color: #646566;
  }

  &__card {
    background: #fff;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 10px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #ebedf0;
  }

  &__card-title {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: #323233;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__card-asset {
    flex-shrink: 0;
    font-size: 12px;
    color: #646566;
  }

  &__icon-btn {
    border: 0;
    background: transparent;
    width: 44px;
    height: 44px;
    min-width: 44px;
    padding: 0;
    border-radius: 50%;
    touch-action: manipulation;

    &--danger {
      color: #ee0a24;
    }
  }

  // 父级 formily 表单把 info 按钮背景强制成蓝色，图标按钮保持透明底
  &__card-head &__icon-btn.van-button--plain {
    background-color: transparent;
    border-color: transparent;
  }

  &__pics {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding-top: 10px;
  }

  &__pic {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  &__pic-empty {
    width: 56px;
    height: 56px;
    border-radius: 4px;
    background: #f2f3f5;
    color: #646566;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__pic-label {
    font-size: 12px;
    color: #646566;
  }

  &__numbers {
    flex: 1;
    min-width: 0;

    ::v-deep .van-cell {
      padding: 4px 0;
      font-size: 13px;
      line-height: 1.5;
      background: transparent;
    }

    ::v-deep .van-cell__title {
      flex: 0 0 52px;
      max-width: 52px;
      color: #646566;
    }

    ::v-deep .van-cell__value {
      color: #323233;
      word-break: break-all;
    }
  }

  &__hyd {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-size: 13px;
    line-height: 1.5;
    color: #323233;
  }

  &__warn {
    margin-left: 6px;
    font-size: 12px;
    font-style: normal;
    color: #d9001b;
    font-weight: 400;
  }

  &__add {
    margin-top: 4px;
    min-height: 44px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
  }
}

/* 弹层 */
.ma-pop {
  &__inner {
    padding: 20px 16px calc(16px + env(safe-area-inset-bottom));

    &--scroll {
      max-height: 78vh;
      overflow-y: auto;
    }
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #323233;
    text-align: center;
    margin-bottom: 6px;
  }

  &__hint {
    font-size: 12px;
    color: #969799;
    text-align: center;
    margin-bottom: 16px;

    // 未勾选时按全量生效，用警示色强化提示
    &--warn {
      color: #ee0a24;
      font-weight: 500;
      background: #fff1f0;
      border-radius: 6px;
      padding: 6px 10px;
      line-height: 18px;
    }
  }

  &__label {
    font-size: 13px;
    color: #646566;
    margin: 12px 0 8px;
  }

  &__footer {
    margin-top: 20px;
  }

  &__quick {
    margin-top: 12px;
    text-align: center;
  }

  &__quick-btn {
    border: 0;
    background: transparent;
    color: #1989fa;
    font-size: 13px;
    padding: 8px 12px;
    min-height: 36px;
    border-radius: 8px;
  }
}

/* 模块数量计数器（van-stepper） */
.ma-num {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0 4px;

  &__label {
    margin-right: 12px;
    font-size: 14px;
    color: #323233;
  }

  // ::v-deep .van-stepper__input {
  //   font-size: 20px;
  //   font-weight: 600;
  //   color: #323233;
  //   background: #fff;
  //   border: 1px solid #dcdee0;
  //   border-radius: 8px;
  //   height: 44px;
  //   width: 72px;
  //   margin: 0 8px;
  // }

  // ::v-deep .van-stepper__minus,
  // ::v-deep .van-stepper__plus {
  //   width: 44px;
  //   height: 44px;
  //   border-radius: 8px;
  //   background: #fff;
  //   border: 1px solid #dcdee0;
  //   color: #323233;

  //   &::before,
  //   &::after {
  //     background-color: currentColor;
  //   }
  // }

  // ::v-deep .van-stepper__minus--disabled,
  // ::v-deep .van-stepper__plus--disabled {
  //   color: #c8c9cc;
  //   background: #f7f8fa;
  //   border-color: #ebedf0;
  // }
}

/* Stepper 下方的提示与错误文案 */
.ma-num__tip {
  margin-top: 8px;
  text-align: center;
  font-size: 12px;
  color: #969799;

  &--error {
    color: #ee0a24;
  }
}

/* 标签式单选（radio 版） */
.ma-radios {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  &__item {
    flex: 1 0 calc(33.33% - 6px);
    min-height: 44px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #dcdee0;
    border-radius: 8px;
    font-size: 13px;
    background: #fff;
    box-sizing: border-box;
  }

  &--wrap &__item {
    flex: 0 0 calc(50% - 4px);
  }
}

/* 隐藏 radio 圆点，改为整块高亮；选中态由 aria-checked 标识 */
.ma-radios ::v-deep .van-radio {
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.ma-radios ::v-deep .van-radio__icon {
  display: none;
}

.ma-radios ::v-deep .van-radio__label {
  color: #323233;
  font-size: 13px;
  margin-left: 0;
  line-height: 1.4;
}

.ma-radios ::v-deep .van-radio[aria-checked="true"] {
  background: #ecf5ff;
  border-radius: 6px;
}

.ma-radios ::v-deep .van-radio[aria-checked="true"] .van-radio__label {
  color: #1989fa;
  font-weight: 600;
}

/* 上传行（van-cell-group） */
.ma-upload {
  display: block;
  padding: 0;

  ::v-deep .van-cell {
    padding: 10px 0;
    font-size: 13px;
    background: transparent;
  }

  ::v-deep .van-cell__title {
    color: #646566;
    flex: 0 0 auto;
    width: 84px;
  }

  ::v-deep .van-cell__value {
    display: flex;
    justify-content: flex-end;
  }
}

/* 偏差显示 */
.ma-dev {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  margin-top: 12px;
  background: #f7f8fa;
  border-radius: 8px;

  &__label {
    font-size: 13px;
    color: #646566;
  }

  &__value {
    font-size: 15px;
    font-weight: 600;

    &--danger {
      color: #ee0a24;
    }

    &--ok {
      color: #07c160;
    }
  }

  &__warn {
    width: 100%;
    font-size: 12px;
    color: #ee0a24;
  }
}
</style>
